import { z } from 'zod';
import { validateCategoryImageFile, validateStoreCategoryPublicDescription } from '@dukanti/shared';

const BIDI_OVERRIDE_RE = /[\u202A-\u202E\u2066-\u2069]/;

const publicDescriptionFieldSchema = z.string().superRefine((value, ctx) => {
  const result = validateStoreCategoryPublicDescription({
    ar: value,
    he: '',
    en: '',
  });
  if (result.ok) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message:
      result.issue === 'too_long'
        ? 'admin.categories.errors.descriptionTooLong'
        : 'admin.categories.errors.descriptionInvalid',
  });
});

export const categoryFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'admin.categories.errors.nameRequired' })
    .max(80, { message: 'admin.categories.errors.nameTooLong' })
    .refine((value) => !BIDI_OVERRIDE_RE.test(value), {
      message: 'admin.categories.errors.nameInvalid',
    }),
  image: z
    .custom<File | null | undefined>(
      (value) => value === null || value === undefined || value instanceof File,
      { message: 'admin.categories.errors.imageInvalidType' },
    )
    .superRefine((file, ctx) => {
      if (!file) return;
      const verdict = validateCategoryImageFile(file);
      if (verdict === 'imageTooLarge') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.categories.errors.imageTooLarge',
        });
      } else if (verdict === 'imageInvalidType') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.categories.errors.imageInvalidType',
        });
      }
    })
    .optional()
    .nullable(),
  publicDescription: z
    .object({
      ar: publicDescriptionFieldSchema,
      he: publicDescriptionFieldSchema,
      en: publicDescriptionFieldSchema,
    })
    .default({ ar: '', he: '', en: '' }),
});

export type CategoryFormValues = z.infer<typeof categoryFormSchema>;
